import { get } from "@ember/object"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookEditCalendar } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import _ from "lodash"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"

let defaultRoutineModels = function (payload, findFn, _userId) {
  return {
    planbook: payload.planbook ? payload.planbook : findFn("planbook", payload.planbookId),
    cardStack: findFn("card-stack", payload.cardStackId),
  }
}

let defaultContext = function (payload, models) {
  // We use get as some of these values might be null/undefined and we want to not break
  return {
    planbookTitle: get(models, "planbook.attributes.title"),
    cardStackId: get(payload, "cardStackId"),
    cardsStackType: get(models, "cardStack.attributes.usedAs"),
    cardId: get(payload, "cardId"),
    date:
      get(payload, "dateString") === "template"
        ? "a daily routine"
        : moment(get(payload, "dateString")).format("ddd, MMM. D, YYYY"),
  }
}

let defaultRoutineActivity = function (_payload, models, userId, verb = "edited") {
  return {
    actor: `user-${userId}`,
    verb,
    object: `planbook-${models.planbook.id}`,
    to: [`planbook-edits:planbook-${models.planbook.id}`],
  }
}

export default {
  ROUTINE_FIRST_EDIT: {
    params: {},
    patches(payload) {
      let planbookPatch

      let oldPlanbookDate = _.find(payload.planbook.attributes.calendar.dates, (date) => {
        return date.attributes.dateString === payload.dateString
      })

      if (oldPlanbookDate) {
        planbookPatch = Patch.create("planbook", payload.planbook.id, "attributes.calendar.dates", {
          id: oldPlanbookDate.id,
        })
          .set("embedded", "attributes.cardStackId", payload.cardStackId)
          .inc(`attributes.calendar.version`, 1)
      } else {
        planbookPatch = Patch.create("planbook", payload.planbook.id)
          .push("attributes.calendar.dates", {
            id: payload.dateString,
            type: "planbook-date-custom",
            attributes: {
              dateString: payload.dateString,
              cardStackId: payload.cardStackId,
            },
          })
          .inc("attributes.calendar.version", 1)
      }

      let routinePatch = Patch.create("card-stack", payload.cardStackId)
        .isCreatePatch()
        .set("attributes.schemaVersion", 1)
        .set(`attributes.cards`, payload.cards)
        .set(`meta.permissions`, ["can-edit-plan", "can-view-plan"])
        .set(`meta.cardsUpdatingFromTemplate`, false)
        .set(`relationships.planbook.data`, {
          type: "planbook",
          id: payload.planbook.id,
        })

      return [routinePatch.patch, planbookPatch.patch]
    },

    undoPatches(payload) {
      let planbookPatch = Patch.create("planbook", payload.planbook.id)
        .pull("attributes.calendar.dates", { id: payload.dateString })
        .inc("attributes.calendar.version", 1)

      return [planbookPatch.patch]
    },
  },

  ROUTINE_ADD_DAY_CARD: {
    params: {
      cardStackId: "string",
      planbookId: "string",
      position: "number",
      cardId: "string",
      value: "string",
      dateString: "string",
    },

    patches(payload) {
      let patch = Patch.create("card-stack", payload.cardStackId)
        .push("attributes.cards", {
          id: payload.cardId,
          type: "card-value-html",
          attributes: {
            position: payload.position,
            value: payload.value,
          },
        })
        .set("meta.cardsUpdatingFromTemplate", false)
      return [patch.patch]
    },

    undoPatches(payload) {
      let patch = Patch.create("card-stack", payload.cardStackId).pull("attributes.cards", {
        id: payload.cardId,
      }).patch

      return [patch]
    },

    narrative(payload, findFn, userId): Narrative {
      let models = defaultRoutineModels(payload, findFn, userId)
      let context = defaultContext(payload, models)
      let formattedDateString = get(context, "date")
      let newEvent: PlanbookEditCalendar = {
        ...createBlankEvent(),
        root_id: payload.planbookId,
        root_type: "planbook",
        primary_object_type: "planbook",
        primary_object_id_and_ancestor_ids: [payload.planbookId],
        primary_verb: "edit",
        primary_object_specifier: "calendar",
        adverbial_verb: "adding",
        adverbial_object_type: "day_card",
      }

      return {
        title: `Added a card to ${formattedDateString}`,
        titleForAnalytics: "Day Card - Add",
        description: "",
        activity: defaultRoutineActivity(payload, models, userId, "created"),
        context,
        newAnalyticsEvents: [newEvent],
      }
    },
  },

  ROUTINE_INTERMEDIATE_UPDATE_CARD_VALUE: {
    params: {
      cardStackId: "string",
      planbookId: "string",
      cardId: "string",
      value: "string",
    },

    patches(payload) {
      let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
        id: payload.cardId,
      }).set("embedded", "attributes.value", payload.value).patch
      return [patch]
    },

    undoPatches: null,

    narrative: null,
  },

  ROUTINE_UPDATE_CARD_VALUE: {
    params: {
      cardStackId: "string",
      planbookId: "string",
      cardId: "string",
      value: "string",
    },
    patches(payload) {
      let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
        id: payload.cardId,
      }).set("embedded", "attributes.value", payload.value).patch
      return [patch]
    },

    // TODO
    undoPatches(payload) {
      let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
        id: payload.cardId,
      }).set("embedded", "attributes.value", payload.originalValue).patch
      return [patch]
    },

    narrative(payload, findFn, userId): Narrative {
      let models = defaultRoutineModels(payload, findFn, userId)
      let context = defaultContext(payload, models)
      let formattedDateString = get(context, "date")
      let newEvent: PlanbookEditCalendar = {
        ...createBlankEvent(),
        root_id: payload.planbookId,
        root_type: "planbook",
        primary_object_type: "planbook",
        primary_object_id_and_ancestor_ids: [payload.planbookId],
        primary_verb: "edit",
        primary_object_specifier: "calendar",
        adverbial_verb: "editing",
        adverbial_object_type: "day_card",
      }

      return {
        title: "Edited day card",
        titleForAnalytics: "Day Card - Edit Value",
        description: `Edited a card on ${formattedDateString}`,
        activity: defaultRoutineActivity(payload, models, userId),
        context,
        newAnalyticsEvents: [newEvent],
      }
    },
  },

  ROUTINE_REMOVE_SECTION: {
    params: {
      cardStackId: "string",
      planbookId: "string",
      card: "object",
    },
    patches(payload) {
      let patch = Patch.create("card-stack", payload.cardStackId).pull("attributes.cards", {
        id: payload.card.id,
      }).patch
      return [patch]
    },

    undoPatches(payload) {
      let patch = Patch.create("card-stack", payload.cardStackId).push(
        "attributes.cards",
        payload.card,
      ).patch
      return [patch]
    },

    narrative(payload, findFn, userId): Narrative {
      let models = defaultRoutineModels(payload, findFn, userId)
      let context = defaultContext(payload, models)
      let formattedDateString = get(context, "date")
      let newEvent: PlanbookEditCalendar = {
        ...createBlankEvent(),
        root_id: payload.planbookId,
        root_type: "planbook",
        primary_object_type: "planbook",
        primary_object_id_and_ancestor_ids: [payload.planbookId],
        primary_verb: "edit",
        primary_object_specifier: "calendar",
        adverbial_verb: "removing",
        adverbial_object_type: "day_card",
      }

      return {
        title: "Removed day card",
        titleForAnalytics: "Day Card - Remove",
        description: `Removed a card from ${formattedDateString}`,
        activity: defaultRoutineActivity(payload, models, userId, "removed"),
        context,
        newAnalyticsEvents: [newEvent],
      }
    },
  },

  ROUTINE_REORDER_SECTIONS: {
    params: {
      cardStackId: "string",
      planbookId: "string",
      positionDiffs: "array",
    },
    patches(payload) {
      return _.map(payload.positionDiffs, (tuple) => {
        return Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
          id: tuple[0],
        }).set("embedded", "attributes.position", tuple[1]).patch
      })
    },

    undoPatches(payload) {
      return _.map(payload.positionDiffs, (tuple) => {
        return Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
          id: tuple[0],
        }).set("embedded", "attributes.position", tuple[2]).patch
      })
    },

    narrative(payload, findFn, userId): Narrative {
      let models = defaultRoutineModels(payload, findFn, userId)
      let context = defaultContext(payload, models)
      let formattedDateString = get(context, "date")
      let newEvent: PlanbookEditCalendar = {
        ...createBlankEvent(),
        root_id: payload.planbookId,
        root_type: "planbook",
        primary_object_type: "planbook",
        primary_object_id_and_ancestor_ids: [payload.planbookId],
        primary_verb: "edit",
        primary_object_specifier: "calendar",
        adverbial_verb: "editing",
        adverbial_object_type: "course_order",
      }

      return {
        title: "Reordered a daily schedule",
        titleForAnalytics: "Routine - Reorder",
        description: `Changed the order of classes and/or cards on ${formattedDateString}`,
        activity: defaultRoutineActivity(payload, models, userId),
        context,
        newAnalyticsEvents: [newEvent],
      }
    },
  },
}
